import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, UsersIcon, GlobeIcon, BuildingIcon, CalendarIcon, ChevronDownIcon, PencilIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { mockBUs, userComparisonData, trendData, countryData, dealerData, buInsights } from '../../data/biData';
const COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const monthsList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
interface BusinessUnitViewProps {
  buName?: string;
  onBuChange?: (bu: string) => void;
}
export function BusinessUnitView({
  buName = 'North America',
  onBuChange
}: BusinessUnitViewProps) {
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [isBuOpen, setIsBuOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const buPickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
      if (buPickerRef.current && !buPickerRef.current.contains(event.target as Node)) {
        setIsBuOpen(false);
      }
    }
    if (isEditingDate || isBuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingDate, isBuOpen]);
  return <div className="space-y-6" data-id="element-681" data-mp="ownnapxn8bb0">
      {/* BU Identity Header — building icon + region stats */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-682" data-mp="ttxi6l3sxf68">
        {/* BU Identity */}
        <div className="flex items-center gap-4 mr-auto" data-id="element-683" data-mp="yta6796essae">
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" data-id="element-684" data-mp="yrpctmvtzfe4">
            <img src="https://cdn.magicpatterns.com/uploads/1HYHcTUzSRJofQ3DFDsYcj/58061.png" alt="Business Unit" className="w-12 h-12 filter invert opacity-90" data-id="element-685" data-mp="13ltjs653jnf7" />
          </div>
          <div data-id="element-686" data-mp="b2wnwadmm7t0">
            {/* Region Select */}
            <div className="relative" ref={buPickerRef} data-id="element-687" data-mp="3kop6ki84tl5">
              <button onClick={() => setIsBuOpen(!isBuOpen)} className="flex items-center gap-1.5 cursor-pointer focus:outline-none group" data-id="element-688" data-mp="3k6lx3x59f3q">
                <span className="text-white font-semibold text-[22px] group-hover:text-blue-200 transition-colors" data-id="element-689" data-mp="3prwp37adk89s">
                  {buName}
                </span>
                <ChevronDownIcon size={18} className={`text-blue-400 transition-transform ${isBuOpen ? 'rotate-180' : ''}`} data-id="element-690" data-mp="3ksk64xmmjlch" />
              </button>
              {isBuOpen && <div className="absolute top-full left-0 mt-1.5 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl py-1 z-50 min-w-[200px]" data-id="element-691" data-mp="3c08ej8frxrux">
                  {mockBUs.map(bu => <button key={bu} onClick={() => {
                onBuChange?.(bu);
                setIsBuOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${bu === buName ? 'bg-blue-600/20 text-white font-medium' : 'text-blue-200 hover:bg-white/10 hover:text-white'}`} data-id="element-692" data-mp="322c82txs7l35">
                      {bu}
                    </button>)}
                </div>}
            </div>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-693" data-mp="3mr3jqzx0a8ek">
              Business Unit quotes analysis and performance
            </p>
            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-694" data-mp="3d1k03s1gyain">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-695" data-mp="3czqw6ki8q4ec">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-696" data-mp="341wxj2x06yo0" />
                <span className="font-medium" data-id="element-697" data-mp="2q25q5l0zsgiz">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-698" data-mp="2u2fw4xosb6xu" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-699" data-mp="sb2tyge7bd5h">
                  <div className="space-y-2.5" data-id="element-700" data-mp="2ctwu9nhivnk5">
                    {/* Period type */}
                    <div data-id="element-701" data-mp="28t36cu42y8r9">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-702" data-mp="28tmmc5a66n2n">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-703" data-mp="2hs82xuw2zs0s">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-704" data-mp="2wg1jpiwwl0ky">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-705" data-mp="1668cb2z747ms">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-706" data-mp="1671ctm7ka0wh">
                          Month
                        </p>
                        <div className="relative" data-id="element-707" data-mp="3b7c53sk3lypu">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-708" data-mp="3b83z4yy1roe0">
                            {monthsList.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-709" data-mp="3u4davflh82uu">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-710" data-mp="7pu15w7g5bup">
                            <ChevronDownIcon size={12} data-id="element-711" data-mp="bsyyzlgl0pd0" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-712" data-mp="2166r6kzcworl">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-713" data-mp="1w9gca0zafz7r">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-714" data-mp="1s9paz3c4y0oz">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-715" data-mp="1n9th3wx5jpn5">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-716" data-mp="33mrqdv2i0k7k">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-717" data-mp="33n05ltm3g1lx">
                        Year
                      </p>
                      <div className="relative" data-id="element-718" data-mp="2ytyodeyezpz6">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-719" data-mp="2786x7etzf7dy">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-720" data-mp="229na3b3njpk0">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-721" data-mp="2uxj8ck8vdpiw">
                          <ChevronDownIcon size={12} data-id="element-722" data-mp="2h0mjupg84k56" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Active Countries Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-723" data-mp="19m9lc0a3y0az">
          <div className="flex items-center gap-3" data-id="element-724" data-mp="1230dwcl5wbok">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-725" data-mp="x1azqo2n7hw9">
              <GlobeIcon size={18} className="text-blue-200/70" data-id="element-726" data-mp="1bybu6zc7xqyf" />
            </div>
            <div data-id="element-727" data-mp="1700jkme3ncf5">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-728" data-mp="171leid4bsxdj">
                Active Countries
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-729" data-mp="jaz6glubnd3j">8</p>
            </div>
          </div>
        </div>

        {/* Active Dealers Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-730" data-mp="t0r2lki2kea4">
          <div className="flex items-center gap-3" data-id="element-731" data-mp="o87rqvarnx90">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-732" data-mp="jy6buhgtbie">
              <BuildingIcon size={18} className="text-blue-200/70" data-id="element-733" data-mp="3rqr7iyc9damx" />
            </div>
            <div data-id="element-734" data-mp="1xsr2duihknv6">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-735" data-mp="1xt97lgn4mcf8">
                Active Dealers
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-736" data-mp="1ts76eu9nfqam">42</p>
            </div>
          </div>
        </div>

        {/* Active Users Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-737" data-mp="1jsyfybdzstt0">
          <div className="flex items-center gap-3" data-id="element-738" data-mp="1eu504cb8gqpo">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-739" data-mp="1evzo5zhswyxz">
              <UsersIcon size={18} className="text-blue-200/70" data-id="element-740" data-mp="1a123twf2n35s" />
            </div>
            <div data-id="element-741" data-mp="16230uhcwhggz">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-742" data-mp="15zgyqx8s3t65">
                Active Users
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-743" data-mp="w3dunlud9kgk">12</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-744" data-mp="r4u7ji41e2mm">
        <BIKPI title="BU Total Quotes" value={450} trend={8.5} icon={<FileTextIcon size={18} data-id="element-746" data-mp="lzgmzhgqjddl" />} color="blue" delay={0.1} data-id="element-745" data-mp="gzc79tnnimof" />
        <BIKPI title="BU Won Quotes" value={280} trend={5.2} icon={<CheckCircleIcon size={18} data-id="element-748" data-mp="qy08jgrf2n5f" />} color="emerald" delay={0.2} data-id="element-747" data-mp="lxm1a0xn3h8g" />
        <BIKPI title="BU Win Rate" value="77.7%" trend={1.5} icon={<PercentIcon size={18} data-id="element-750" data-mp="vwbhzqdsuvn4" />} color="teal" delay={0.3} data-id="element-749" data-mp="qwp4dfu27dds" />
        <BIKPI title="Active Quotes" value={70} icon={<ClockIcon size={18} data-id="element-752" data-mp="3ugtmvvs0rg0n" />} color="amber" delay={0.4} data-id="element-751" data-mp="3ufsa9mvculgt" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-753" data-mp="398pbevt1f4s">
        <BICard title="BU Quotes Trend" className="lg:col-span-2" data-id="element-754" data-mp="87sb9faeeqx5">
          <div className="h-80" data-id="element-755" data-mp="cys7sjflv6z4">
            <ResponsiveContainer width="100%" height="100%" data-id="element-756" data-mp="cz0pqpql52h6">
              <LineChart data={trendData.map(d => ({
              ...d,
              total: Math.floor(d.total * 0.4),
              won: Math.floor(d.won * 0.4),
              lost: Math.floor(d.lost * 0.4)
            }))} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-757" data-mp="3bosf23x9off3">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-758" data-mp="33nbbdhwmlg9a" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-759" data-mp="3ibxodcys5vxc" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-760" data-mp="3df7amwag7cf3" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-761" data-mp="2ppcykgxydrvw" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-762" data-mp="2zn96z0zia8qb" />
                <Line type="monotone" dataKey="total" name="Total Quotes" stroke="#3b82f6" strokeWidth={3} dot={{
                r: 4,
                fill: '#3b82f6',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} activeDot={{
                r: 6
              }} data-id="element-763" data-mp="2uphgmbeieuo8" />
                <Line type="monotone" dataKey="won" name="Won Quotes" stroke="#10b981" strokeWidth={3} dot={{
                r: 4,
                fill: '#10b981',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} data-id="element-764" data-mp="hiritxdsoobq" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Quotes by User" data-id="element-765" data-mp="106oa3e28r5zv">
          <div className="h-72" data-id="element-766" data-mp="107y86393ge6i">
            <ResponsiveContainer width="100%" height="100%" data-id="element-767" data-mp="1569g28ftwem3">
              <BarChart data={userComparisonData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-768" data-mp="155r9oizgck0g">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-769" data-mp="vfejob8vbymx" />
                <XAxis dataKey="name" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-770" data-mp="1j5sc42vd7zi1" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-771" data-mp="1nxsk8ill9ypp" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-772" data-mp="8pd33ys22jh" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-773" data-mp="3ie22pwr0rti7" />
                <Bar dataKey="won" name="Won" stackId="a" fill="#10b981" radius={[0, 0, 4, 4]} data-id="element-774" data-mp="iz6pfxzaylio" />
                <Bar dataKey="active" name="Active" stackId="a" fill="#3b82f6" data-id="element-775" data-mp="dzkczqrat9ax" />
                <Bar dataKey="lost" name="Lost" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} data-id="element-776" data-mp="98vlibfmccbs" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Win Rate by User" data-id="element-777" data-mp="11peoa3vyypt2">
          <div className="h-72" data-id="element-778" data-mp="11qgb15mcl0qm">
            <ResponsiveContainer width="100%" height="100%" data-id="element-779" data-mp="11rj2pfrhv77l">
              <BarChart data={userComparisonData} layout="vertical" margin={{
              top: 20,
              right: 30,
              left: 60,
              bottom: 5
            }} data-id="element-780" data-mp="wxn7um6j20gn">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} data-id="element-781" data-mp="2kjrccsgp0zxb" />
                <XAxis type="number" domain={[0, 100]} stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-782" data-mp="2olmjwep7w9jj" />
                <YAxis dataKey="name" type="category" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-783" data-mp="2tm0kxcseij82" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} formatter={value => [`${value}%`, 'Win Rate']} data-id="element-784" data-mp="2yk23zxptuijj" />
                <Bar dataKey="winRate" name="Win Rate" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} data-id="element-785" data-mp="3cbyszsw78cbp">
                  {userComparisonData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.winRate > 80 ? '#10b981' : entry.winRate < 75 ? '#ef4444' : '#8b5cf6'} data-id="element-786" data-mp="3h8p7wcw9p1vj" data-mpi={index} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-787" data-mp="1h6h92fep1r4g">
        <div className="lg:col-span-2 space-y-6" data-id="element-788" data-mp="1vyi1ajam2ahd">
          <BICard title="User Performance" data-id="element-789" data-mp="1vz9ph91vp5xm">
            <div className="overflow-x-auto" data-id="element-790" data-mp="1r5oxbxmyv66c">
              <table className="w-full text-sm text-left" data-id="element-791" data-mp="1r4x1clpgj6fj">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-792" data-mp="24wtqcgvtx07p">
                  <tr data-id="element-793" data-mp="20vrortefwc2k">
                    <th className="px-4 py-3 font-medium" data-id="element-794" data-mp="20ym9lm1d7ywi">User</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-795" data-mp="2euzqou1zlyag">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-796" data-mp="29wy8ebcdy301">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-797" data-mp="ki9ta2ceu6yb">Active</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-798" data-mp="kke0ou9ipo3c">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-799" data-mp="2r725lddsd9cn">
                  {userComparisonData.map((user, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-800" data-mp="2w66cfxd8muzt" data-mpi={idx}>
                      <td className="px-4 py-3 font-medium text-white flex items-center" data-id="element-801" data-mp="2hhijzzyov1e3">
                        <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs mr-2 font-bold" data-id="element-802" data-mp="2mfbnumcirjb7">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-803" data-mp="1yixxkl7qsk6s">
                        {user.total}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-400" data-id="element-804" data-mp="23h7qsq9wpapr">
                        {user.won}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-400" data-id="element-805" data-mp="1pcoh19jiik7">
                        {user.active}
                      </td>
                      <td className="px-4 py-3 text-right text-white" data-id="element-806" data-mp="2lkc36avto697">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.winRate > 80 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`} data-id="element-807" data-mp="2gmaglfz6tosz">
                          {user.winRate}%
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>

          <BICard title="Top Dealers in BU" data-id="element-808" data-mp="1xw2kvixti0mt">
            <div className="overflow-x-auto" data-id="element-809" data-mp="1xv9lj316udep">
              <table className="w-full text-sm text-left" data-id="element-810" data-mp="1swyawq32jyvf">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-811" data-mp="1sxhtm90jmrai">
                  <tr data-id="element-812" data-mp="1nzg0ev9elblb">
                    <th className="px-4 py-3 font-medium" data-id="element-813" data-mp="1o07v83v6f5aj">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-814" data-mp="3nah45nq2gvax">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-815" data-mp="3ibmlqmw0h8rc">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-816" data-mp="3pvotottboqng">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-817" data-mp="3ptkswkqq07re">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-818" data-mp="3gcjusaua8e5">
                  {dealerData.filter(d => d.bu === 'North America' || d.bu === 'LATAM').map((dealer, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-819" data-mp="8h07r53ru945" data-mpi={idx}>
                        <td className="px-4 py-3 font-medium text-white" data-id="element-820" data-mp="36yxfgysn11q9">
                          {dealer.name}
                        </td>
                        <td className="px-4 py-3 text-blue-300" data-id="element-821" data-mp="3guh7vb7ztia9">
                          {dealer.country}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-100" data-id="element-822" data-mp="3lv4xptkhjt2w">
                          {dealer.total}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-400" data-id="element-823" data-mp="113co6j7oppz5">
                          {dealer.won}
                        </td>
                        <td className="px-4 py-3 text-right text-white" data-id="element-824" data-mp="2bhefk9lekje3">
                          {dealer.winRate}%
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-825" data-mp="2p9kll2uud9zt">
          <BIInsights insights={buInsights} data-id="element-826" data-mp="2p8j8yty6gffz" />
        </div>
      </div>
    </div>;
}